import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    OPERATIONS_ACCESS_COPY,
    type OperationsAccess,
} from '../../operationsAccess';

type AccessNoticeProps = {
    /** Never `full` — the page renders the catalog in that case. */
    access: Exclude<OperationsAccess, { kind: 'full' }>;
};

/**
 * Why the operation catalog is unavailable — or only partly available — to this
 * session.
 *
 * Rendered with no request having been made. A PIN/kiosk token 403s on every
 * accounting endpoint whatever role it holds, so a screen that fetches in order
 * to discover that shows a failure where the truth is a permission.
 *
 * The three cases read differently on purpose. A kiosk session has a remedy the
 * user owns — sign in with a password. A Seller does not, and is told so plainly
 * rather than invited to retry. A BranchAdmin is neither: the catalog is theirs
 * to read, so the notice sits *above* it as a note about what they cannot
 * change, and the page renders normally underneath.
 */
export const AccessNotice: React.FC<AccessNoticeProps> = ({ access }) => {
    const { t } = useTranslation('pages');
    const copy = OPERATIONS_ACCESS_COPY[access.kind];

    return (
        <div
            className="flex flex-col items-center gap-2 rounded-2xl border border-separator bg-surface px-6 py-12 text-center"
            {...getNodeTestIdProp(`operations-access-${access.kind}`)}
        >
            <Icon
                aria-hidden
                className="size-7 text-muted"
                icon="gravity-ui:lock"
            />
            <span className="text-sm font-medium text-foreground">
                {t(copy.titleKey)}
            </span>
            <span className="max-w-md text-sm text-muted">
                {t(copy.bodyKey)}
            </span>
        </div>
    );
};
